import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faInbox,
    faTruck,
    faCheckCircle,
    faFlask,
    faDatabase,
    faTimes,
    faCalendarAlt,
    faSave
} from '@fortawesome/free-solid-svg-icons';
import './SampleDetail.css';

const SampleDetail = ({ sample, onClose, onSaveChanges }) => {
    const [editedSample, setEditedSample] = useState(sample ? { ...sample } : null);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    
    if (!sample) return null;

    // Status icons mapping
    const statusIcons = {
        'Order Received': faInbox,
        'Sent Cargo': faTruck,
        'Cargo Delivered': faCheckCircle,
        'Lab Received': faFlask,
        'Data Delivered': faDatabase
    };

    // Status colors mapping
    const statusColors = {
        'Order Received': '#f0ad4e',
        'Sent Cargo': '#5bc0de',
        'Cargo Delivered': '#5cb85c',
        'Lab Received': '#428bca',
        'Data Delivered': '#d9534f'
    };

    // Calculate progress percentage
    const getProgressPercentage = () => {
        const stageOrder = ['Order Received', 'Sent Cargo', 'Cargo Delivered', 'Lab Received', 'Data Delivered'];
        const currentStageIndex = stageOrder.indexOf(sample.status.currentStage);
        return Math.round((currentStageIndex / (stageOrder.length - 1)) * 100);
    };

    // Handle date change
    const handleDateChange = (stage, dateValue) => {
        setEditedSample(prev => {
            const newStages = { ...prev.status.stages, [stage]: dateValue ? new Date(dateValue).toISOString() : null };
            return {
                ...prev,
                status: {
                    ...prev.status,
                    stages: newStages
                }
            };
        });
    };

    // Format date for input
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // Format date to a readable string
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Handle save changes
    const handleSaveChanges = async () => {
        console.log('SampleDetail: Starting save operation');
        try {
            setIsSaving(true);
            setError(null);
            
            // Map stages to API compatible format
            const stages = {
                "Order Received": editedSample.status.stages["Order Received"],
                "Sent Cargo": editedSample.status.stages["Sent Cargo"],
                "Cargo Delivered": editedSample.status.stages["Cargo Delivered"],
                "Lab Received": editedSample.status.stages["Lab Received"],
                "Data Delivered": editedSample.status.stages["Data Delivered"]
            };
            
            console.log('SampleDetail: Sending API request');
            const response = await fetch(`https://service1.szapfs.org/orders/${editedSample.barcode}/update-all-statuses`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ stages }),
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to update sample');
            }
            
            console.log('SampleDetail: API request successful, calling onSaveChanges');
            
            // Store onSaveChanges in a variable before closing the modal
            const saveChangesCallback = onSaveChanges;
            
            // First close the modal
            onClose();
            
            // Then trigger the refresh after modal is closed
            // Use a proper callback pattern instead of relying on closure variables
            // that might be nullified when component unmounts
            if (saveChangesCallback) {
                console.log('SampleDetail: Triggering refresh callback');
                saveChangesCallback();
            }
            
        } catch (err) {
            setError(err.message || 'An error occurred while saving changes');
            console.error('Error saving changes:', err);
        } finally {
            setIsSaving(false);
        }
    };

    // Create modal content to render directly to document body
    const modalContent = (
        <div className="samplemanager-detail-overlay">
            <div className="samplemanager-detail-modal">

                <div className="samplemanager-detail-header" >
                    <div className="samplemanager-detail-icon">
                        <FontAwesomeIcon icon={statusIcons[sample.status.currentStage]} />
                    </div>
                    <div className="samplemanager-detail-title">
                        <h3>Sample Details</h3>
                        <p className="samplemanager-detail-status">{sample.status.currentStage} - {getProgressPercentage()}% Complete</p>
                    </div>
                    <button className="samplemanager-detail-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="samplemanager-detail-body">
                    {error && (
                        <div className="samplemanager-detail-error">
                            {error}
                        </div>
                    )}
                    <div className="samplemanager-detail-content">
                        <div className="samplemanager-detail-flex-container">
                            <div className="samplemanager-detail-left-column">
                                <div className="samplemanager-detail-section">
                                    <h4>Patient Information</h4>
                                    <div className="samplemanager-detail-grid">
                                        <div className="samplemanager-detail-item">
                                            <label>Name</label>
                                            <p>{sample.firstName} {sample.lastName}</p>
                                        </div>
                                        <div className="samplemanager-detail-item">
                                            <label>Patient ID</label>
                                            <p>{sample.patientId}</p>
                                        </div>
                                        <div className="samplemanager-detail-item">
                                            <label>Address</label>
                                            <p>
                                                {(() => {
                                                    // Handle case where address is a JSON string
                                                    if (typeof sample.address === 'string') {
                                                        try {
                                                            // Try to parse as JSON
                                                            const addressObj = JSON.parse(sample.address);
                                                            
                                                            // If successfully parsed as object with address fields
                                                            if (addressObj && typeof addressObj === 'object') {
                                                                const parts = [];
                                                                
                                                                // Add address lines
                                                                if (addressObj.address1) parts.push(addressObj.address1);
                                                                if (addressObj.address2) parts.push(addressObj.address2);
                                                                
                                                                // Add city, province
                                                                let cityProvince = '';
                                                                if (addressObj.city) cityProvince = addressObj.city;
                                                                if (addressObj.province && addressObj.province.trim() !== '') 
                                                                    cityProvince += cityProvince ? `, ${addressObj.province}` : addressObj.province;
                                                                if (cityProvince) parts.push(cityProvince);
                                                                
                                                                // Add zip and country
                                                                if (addressObj.zip) parts.push(addressObj.zip);
                                                                if (addressObj.country && addressObj.country.trim() !== '') parts.push(addressObj.country);
                                                                
                                                                return parts.join(', ');
                                                            }
                                                        } catch (e) {
                                                            // Not JSON, just return the string
                                                            return sample.address;
                                                        }
                                                    }
                                                    
                                                    // Handle case where address is already an object
                                                    if (sample.address && typeof sample.address === 'object') {
                                                        const addr = sample.address;
                                                        const parts = [];
                                                        
                                                        if (addr.address1) parts.push(addr.address1);
                                                        if (addr.address2) parts.push(addr.address2);
                                                        
                                                        let cityProvince = '';
                                                        if (addr.city) cityProvince = addr.city;
                                                        if (addr.province && addr.province.trim() !== '') 
                                                            cityProvince += cityProvince ? `, ${addr.province}` : addr.province;
                                                        if (cityProvince) parts.push(cityProvince);
                                                        
                                                        if (addr.zip) parts.push(addr.zip);
                                                        if (addr.country && addr.country.trim() !== '') parts.push(addr.country);
                                                        
                                                        return parts.join(', ');
                                                    }
                                                    
                                                    // Fallback
                                                    return sample.address || 'No address available';
                                                })()}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="samplemanager-detail-section">
                                    <h4>Sample Information</h4>
                                    <div className="samplemanager-detail-grid">
                                        <div className="samplemanager-detail-item">
                                            <label>Barcode</label>
                                            <p>{sample.barcode}</p>
                                        </div>
                                        <div className="samplemanager-detail-item">
                                            <label>Kit Type</label>
                                            <p>{sample.kitType}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="samplemanager-detail-right-column">
                                <div className="samplemanager-detail-section">
                                    <h4>Status Timeline</h4>
                                    <div className="samplemanager-detail-timeline">
                                        {Object.entries(editedSample.status.stages).map(([stage, date], index, array) => {
                                            const isLast = index === array.length - 1;
                                            const isCurrent = stage === sample.status.currentStage;
                                            const isPast = Object.keys(editedSample.status.stages).indexOf(stage) < 
                                                          Object.keys(editedSample.status.stages).indexOf(sample.status.currentStage);
                                            
                                            return (
                                                <div key={index} className={`samplemanager-detail-timeline-item ${isCurrent ? 'current' : ''} ${isPast ? 'completed' : ''}`}>
                                                    <div className="samplemanager-detail-timeline-connector">
                                                        <div className="samplemanager-detail-timeline-dot"></div>
                                                        {!isLast && <div className="samplemanager-detail-timeline-line"></div>}
                                                    </div>
                                                    <div className="samplemanager-detail-timeline-content">
                                                        <FontAwesomeIcon 
                                                            icon={statusIcons[stage]} 
                                                            className="samplemanager-detail-stage-icon"
                                                        />
                                                        <span className="samplemanager-detail-stage-name">{stage}</span>
                                                        <div className="samplemanager-detail-stage-date">
                                                            <input 
                                                                type="datetime-local" 
                                                                value={formatDateForInput(date)}
                                                                onChange={(e) => handleDateChange(stage, e.target.value)}
                                                                onClick={(e) => e.currentTarget.showPicker()}
                                                                className="samplemanager-detail-date-input"
                                                                title="Edit date and time"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="samplemanager-detail-footer">
                        <button 
                            className="samplemanager-detail-save" 
                            onClick={handleSaveChanges}
                            disabled={isSaving}
                        >
                            <FontAwesomeIcon icon={faSave} />
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
    
    // Use createPortal to render modal directly to document body
    return createPortal(modalContent, document.body);
};

export default SampleDetail;
